import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { useFlowium } from '../client/FlowiumContext';

import AuthLayout from '../layout/AuthLayout';
import Button from '../components/Button';
import Input from '../components/Input';
import { GOOGLE_AUTH_CLIENT_ID } from '../const';
import { EnvelopeIcon, LockIcon } from '../components/Icons';
import { getErrorMessage } from '../utils';

const Login: React.FC = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [googleTokenId, setGoogleTokenId] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { login, client } = useFlowium();

    useEffect(() => {
        if (googleTokenId) {
            handleLogin();
        }
    }, [googleTokenId])

    const isValidEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleLogin = async () => {
        if ((!email || !password) && !googleTokenId) {
            setError('Please enter your email and password or use Google to login');
            return;
        }
        if (!googleTokenId && !isValidEmail(email)) {
            setError('Please enter a valid email address');
            return;
        }
        setLoading(true);
        try {
            await login({ email, password, googleTokenId });
            navigate('/browser');
        } catch (error: unknown) {
            const message = getErrorMessage(error);
            if (message.includes('already registered with Google login')) {
                setError('This email is registered with Google. Please use the "Continue with Google" button below.');
            } else if (message.includes('already registered with email/password')) {
                setError('This email is registered with email/password. Please enter your password above.');
            } else if (message.toLowerCase().includes('email not verified')) {
                setError(message);
            } else {
                setError('Invalid email or password');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && email && password) {
            handleLogin();
        }
    };

    return (
        <GoogleOAuthProvider clientId={GOOGLE_AUTH_CLIENT_ID}>
            <AuthLayout title="Welcome to Flowium">
                <Input
                    label="Email"
                    placeholder="winston@churchill.co"
                    icon={<EnvelopeIcon />}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    className="mb-4 mt-6"
                />
                <Input
                    label="Password"
                    type="password"
                    placeholder="********"
                    icon={<LockIcon />}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={loading}
                    className="mb-1"
                />
                <div className="w-full flex justify-end">
                    <Link to="/password-recovery" className="text-accent hover:underline text-sm">
                        Forgot password?
                    </Link>
                </div>
                <Button
                    onClick={handleLogin}
                    disabled={loading || !email || !password}
                    className='w-full mt-8'
                >
                    {loading ? 'Logging in...' : 'Login'}
                </Button>
                {error && <p className="text-error mt-4 text-sm">{error}</p>}
                <p className="mt-4 text-center text-sm font-light">
                    <span className="text-content-secondary">First time here?</span>
                    <button onClick={() => navigate('/register')} className="ml-1 text-accent hover:underline">
                         Sign up
                    </button>
                </p>
                <div className="mt-8 text-center">
                    <div className="text-content-tertiary text-xs mb-4">
                        — or continue with —
                    </div>
                    <GoogleLogin
                        onSuccess={(credentialResponse) => {
                            setGoogleTokenId(credentialResponse.credential ?? '');
                        }}
                        onError={() => setError('Google login failed')}
                        useOneTap
                        width="100%"
                    />
                </div>
                <div className="mt-6 text-center">
                    <p className="text-xs text-content-tertiary">
                        <strong>Note:</strong> You can only use the same authentication method you used to create your account.
                    </p>
                </div>
            </AuthLayout>
        </GoogleOAuthProvider>
    );
};

export default Login;
